import axios from 'axios';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { environment } from '../../../environments/environment';

// Create axios instance with base configuration
export const apiClient = axios.create({
  baseURL: environment.apiUrl,
  timeout: 120000, // 2 minutes for operations like verify that can take longer
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: Add Cognito JWT token
apiClient.interceptors.request.use(
  async (config) => {
    try {
      // Get current session and tokens (Amplify v6)
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      const payload = session.tokens?.accessToken?.payload;

      if (!token || !payload) {
        throw new Error('No valid session found');
      }

      // Add Authorization header with Bearer token
      // The backend authorizer will extract user context from the JWT
      config.headers.Authorization = `Bearer ${token}`;

      console.log('API Request:', {
        method: config.method,
        url: config.url,
        userId: payload.sub,
        userGroup: payload['custom:group'],
      });
    } catch (error) {
      console.error('Failed to attach auth token:', error);
      // Don't throw here - let the request proceed and handle 401 in response interceptor
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Extracts the tenant ID from a mapping's labels array.
 * The backend encodes it as "pl.orgRequester:{tenantId}".
 * Call once when the mapping is loaded; pass the result around as a plain string.
 */
export function tenantIdFromLabels(labels?: string[]): string | undefined {
  if (!labels) return undefined;
  const prefix = 'pl.orgRequester:';
  const label = labels.find((l) => l.startsWith(prefix));
  return label ? label.slice(prefix.length) : undefined;
}

/**
 * Returns Axios config that injects x-tenant-id for a specific call.
 * Usage: apiClient.get(url, withTenantId(tenantId))
 */
export function withTenantId(tenantId?: string): { headers?: Record<string, string> } {
  return tenantId ? { headers: { 'x-tenant-id': tenantId } } : {};
}

// Response interceptor: Handle authentication errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response?.status === 401) {
      console.error('Unauthorized request - redirecting to login');
      // Clear local session
      try {
        await signOut();
      } catch (signOutError) {
        console.error('Error signing out:', signOutError);
      }
      // Redirect to login page
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);
